/**
 * 地図共通処理
 */

import Util from "./util";
import geodesic from "./geodesic.js";

export default {
  calcBaseTime,
  getBpLineDatas,
  getNearTown,
  getRoundTimeList,
};

/**
 * 基準日時の計算
 * @param {*} appTime 観測日時
 * @param {*} mapNum 地図番号(0～3)
 * @param {*} interval 表示間隔
 */
function calcBaseTime(appTime, mapNum, interval) {
  let time = Util.toDate(appTime);
  let times = [];
  for (let i = 0; i < 4; i++) {
    times.push(time);
    time = Util.subMinutes(time, interval).toDate();
  }
  return times[mapNum];
}

/**
 * 破堤点ライン描画用に座標リストを２次元配列で返却
 * @param {*} bp180min webストレージの3時間以内に浸水する破堤点にリスト
 * @param {*} breakPointInterval 破堤点ラインを分割する間隔（km）
 */
function getBpLineDatas(bp180min, breakPointInterval) {
  // 河川コード毎にmapに格納
  let mapRvr = new Map();
  for (let bp of bp180min) {
    if (mapRvr.has(bp.riverCd)) {
      mapRvr.get(bp.riverCd).push(bp);
    } else {
      mapRvr.set(bp.riverCd, [bp]);
    }
  }

  // 河川コード+左右岸区分毎にmapに格納
  let mapRvrBank = new Map();
  for (let rvrValue of mapRvr.values()) {
    for (let bp of rvrValue) {
      // 破堤点からの位置を左右岸区分・河口からの距離に分離
      let loc = getLocation(bp.bpLocation);
      loc.latlon = bp.latlon;
      // 返却値の見通しが悪い場合は、下記を返却値に追加する
      loc.riverName = bp.riverName;
      // loc.riverCd = bp.riverCd;
      var key = bp.riverCd + loc.bank;
      if (mapRvrBank.has(key)) {
        mapRvrBank.get(key).push(loc);
      } else {
        mapRvrBank.set(key, [loc]);
      }
    }
  }

  let result = [];

  // 返却リストを生成
  for (let rvr of mapRvr.keys()) {
    // 左岸を「河口からの距離」の昇順で格納
    if (mapRvrBank.has(rvr + "L")) {
      let listSortL = mapRvrBank.get(rvr + "L").sort(compareBpLocation);
      let listLLatlon = [];
      for (let idx = 0; idx < listSortL.length; idx++) {
        if (idx === 0) {
          listLLatlon.push(listSortL[idx].latlon);
        } else {
          // 2点間の距離をキロメートル単位で取得
          let distance = geodesic.getDistance(
            listSortL[idx - 1].latlon,
            listSortL[idx].latlon
          );

          if (!isNaN(distance) && distance < breakPointInterval) {
            // 100m未満の場合は追加
            listLLatlon.push(listSortL[idx].latlon);
          } else {
            // 2点以上登録されている場合は返却値に追加
            if (listLLatlon.length >= 2) {
              result.push(listLLatlon.concat());
            }
            // 新点をリストの起点にする
            listLLatlon = [listSortL[idx].latlon];
          }
        }
      }
      if (listLLatlon.length >= 2) {
        result.push(listLLatlon);
      }
    }
    // 右岸を「河口からの距離」の昇順で格納
    if (mapRvrBank.has(rvr + "R")) {
      let listSortR = mapRvrBank.get(rvr + "R").sort(compareBpLocation);
      let listRLatlon = [];
      for (let idx = 0; idx < listSortR.length; idx++) {
        if (idx === 0) {
          listRLatlon.push(listSortR[idx].latlon);
        } else {
          // 2点間の距離をキロメートル単位で取得
          let distance = geodesic.getDistance(
            listSortR[idx - 1].latlon,
            listSortR[idx].latlon
          );

          if (!isNaN(distance) && distance < breakPointInterval) {
            // 100m未満の場合は追加
            listRLatlon.push(listSortR[idx].latlon);
          } else {
            // 2点以上登録されている場合は返却値に追加
            if (listRLatlon.length >= 2) {
              result.push(listRLatlon.concat());
            }
            // 新点をリストの起点にする
            listRLatlon = [listSortR[idx].latlon];
          }
        }
      }
      if (listRLatlon.length >= 2) {
        result.push(listRLatlon);
      }
    }
    // 左右岸が無い場合は100m毎に格納
    if (!mapRvrBank.has(rvr + "L") && !mapRvrBank.has(rvr + "R")) {
      let listDis = mapRvrBank.get(rvr + "-").sort(compareBpLocation);
      let listResult = [];
      for (let idx = 0; idx < listDis.length; idx++) {
        if (idx === 0) {
          listResult.push(listDis[idx].latlon);
        } else {
          // 2点間の距離をキロメートル単位で取得
          let distance = geodesic.getDistance(
            listDis[idx - 1].latlon,
            listDis[idx].latlon
          );
          if (!isNaN(distance) && distance < breakPointInterval) {
            // 100m未満の場合は追加
            listResult.push(listDis[idx].latlon);
          } else {
            // 2点以上登録されている場合は返却値に追加
            if (listResult.length >= 2) {
              result.push(listResult.concat());
            }
            // 新点をリストの起点にする
            listResult = [listDis[idx].latlon];
          }
        }
      }
      // 最後のリストを返却値に追加
      if (listResult.length >= 2) {
        result.push(listResult.concat());
      }
    }
  }
  return result;
}

/**
 * 破堤点からの位置を、左右岸区分・河口からの距離に分離して返却
 * @param {*} bpLocation 破堤点の位置（08.60km左岸破堤）
 * @returns {bank:左右岸区分(L,R,-), disMouth:河口からの距離}
 */
function getLocation(bpLocation) {
  let bank = "-";

  // 「08.60km左岸破堤」の様なパターン
  if (bpLocation.indexOf("左岸") != -1) {
    bank = "L";
  } else if (bpLocation.indexOf("右岸") != -1) {
    bank = "R";
  }
  // 「L08k60」の様なパターン
  else if (bpLocation.indexOf("L") === 0) {
    bank = "L";
  } else if (bpLocation.indexOf("R") === 0) {
    bank = "R";
  }

  let disMouth = 0;
  // 「08.60km左岸破堤」の様なパターン
  if (bpLocation.indexOf("km") != -1) {
    let list = bpLocation.split("km");
    let str = list[0].trim();
    if (!Number.isNaN(str)) {
      disMouth = Number(str);
    }
  }
  // 「L08k60」の様なパターン
  else if (bpLocation.indexOf("k") != -1) {
    let strLoc = bpLocation.replace("L", "").replace("R", "").trim();
    let list = strLoc.split("k");
    if (list.length === 2) {
      let str = list[0] + "." + list[1];
      if (!Number.isNaN(str)) {
        disMouth = Number(str);
      }
    }
  }
  return { bank: bank, disMouth: disMouth, bpLocation: bpLocation };
}

// 距離の分割ができないパターンがあるので、下記の文字列比較を使用
// /**
//  * disMouthでのsort用
//  * @param {}} a
//  * @param {*} b
//  */
// function compareDisMouth(a, b) {
//   return a.disMouth - b.disMouth;
// }

/**
 * bpLocationでのsort用
 * @param {}} a
 * @param {*} b
 */
function compareBpLocation(a, b) {
  let result = -1;
  if (a.bpLocation > b.bpLocation) {
    result = 1;
  }
  return result;
}

/**
 * 指定座標に一番近い市町村を取得
 * @param {*} center 対象座標
 * @param {*} latLngListByPref 都道府県別市町村座標リスト
 * @param {*} viewPrefs 表示都道府県リスト
 */
function getNearTown(center, latLngListByPref, viewPrefs) {
  let prefCd = "";
  let prefNm = "";
  let townCd = "";
  let townNm = "";
  let townList = [];
  let distanceDiff = 10 * Math.pow(10, 9);

  // 表示都道府県リストの中から検索
  latLngListByPref.forEach((pref) => {
    // 表示都道府県一覧に含まれる県のみ対象とする
    if (
      viewPrefs.find((tgt) => {
        return tgt.id == pref.prefCd;
      })
    ) {
      pref.twnList.forEach((town) => {
        // 中心座標が含まれる市町村を検索してリストに追加
        let pushFlg = true;
        if (town.maxLat * Math.pow(10, 9) < center[0] * Math.pow(10, 9)) {
          pushFlg = false;
        } else if (
          town.maxLon * Math.pow(10, 9) <
          center[1] * Math.pow(10, 9)
        ) {
          pushFlg = false;
        } else if (
          town.minLat * Math.pow(10, 9) >
          center[0] * Math.pow(10, 9)
        ) {
          pushFlg = false;
        } else if (
          town.minLon * Math.pow(10, 9) >
          center[1] * Math.pow(10, 9)
        ) {
          pushFlg = false;
        }
        if (pushFlg) {
          townList.push(town);
        }
      });
    }
  });

  // 複数存在する場合は、中心に一番近い市町村とする
  townList.forEach((town) => {
    let targetDiff =
      Math.abs(center[0] * Math.pow(10, 9) - town.lat * Math.pow(10, 9)) +
      Math.abs(center[1] * Math.pow(10, 9) - town.lon * Math.pow(10, 9));
    if (distanceDiff > targetDiff) {
      distanceDiff = targetDiff;
      prefCd = town.prefCd;
      prefNm = town.prefNm;
      townCd = town.twnCd;
      townNm = town.twnNm;
    }
  });

  if (townList.length == 0) {
    // 上記の処理で取得出来なかった場合のみ、
    // 表示都道府県の中で一番中央が近い市町村を設定する
    latLngListByPref.forEach((pref) => {
      if (
        viewPrefs.find((tgt) => {
          return tgt.id == pref.prefCd;
        })
      ) {
        pref.twnList.forEach((town) => {
          let targetDiff =
            Math.abs(center[0] * Math.pow(10, 9) - town.lat * Math.pow(10, 9)) +
            Math.abs(center[1] * Math.pow(10, 9) - town.lon * Math.pow(10, 9));
          if (distanceDiff > targetDiff) {
            distanceDiff = targetDiff;
            prefCd = town.prefCd;
            prefNm = town.prefNm;
            townCd = town.twnCd;
            townNm = town.twnNm;
          }
        });
      }
    });
  }

  return {
    prefCd: prefCd,
    prefNm: prefNm,
    townCd: townCd,
    townNm: townNm,
    townList: townList,
  };
}

/**
 * 履歴動画再生時間リスト(観測所、予警報用)の生成
 * @param {*} historyPlayList レーダの履歴動画再生時間リスト
 * @param {*} shortIntervalDay 5分間隔対象期間
 * @param {*} crntDate 最新観測日時
 */
function getRoundTimeList(historyPlayList, shortIntervalDay, crntDate) {
  let roundTimeList = [];
  historyPlayList.forEach((playTime) => {
    const roundTime = Util.roundMinutes(
      Util.toDate(playTime),
      shortIntervalDay,
      crntDate
    );
    // 文字列に変換
    const roundTimeStr = Util.formatDate(roundTime, "YYYY-MM-DD HH:mm");
    // 既に同じ日時が存在していれば格納しない
    if (!roundTimeList.includes(roundTimeStr)) {
      roundTimeList.push(roundTimeStr);
    }
  });
  return roundTimeList;
}
